import type { UnitCategory } from "@/types";

/**
 * Normalize an area value for sorting. The SE API uses -1 as a "no upper bound"
 * sentinel, and some categories may be missing area data. Both sort last.
 */
const areaSortKey = (value: number | null | undefined): number =>
    value === undefined || value === null || value === -1
        ? Number.POSITIVE_INFINITY
        : value;

const nameCompare = (a: UnitCategory, b: UnitCategory): number =>
    (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase());

/**
 * Group ordering for the default sort: "size" categories first, then
 * "unit-type", then anything else.
 */
const groupRank = (category: UnitCategory): number => {
    if (category.type === "size") return 0;
    if (category.type === "unit-type") return 1;
    return 2;
};

/**
 * Default category order:
 * 1. "size" categories ascending by min_area (max_area tiebreaker; the -1
 *    "no upper bound" sentinel and missing values sort last)
 * 2. "unit-type" categories alphabetically by name (case-insensitive)
 * 3. any other types last, alphabetically by name
 */
const defaultCompare = (a: UnitCategory, b: UnitCategory): number => {
    const rankDiff = groupRank(a) - groupRank(b);
    if (rankDiff !== 0) return rankDiff;

    if (groupRank(a) === 0) {
        const minDiff = areaSortKey(a.min_area) - areaSortKey(b.min_area);
        if (minDiff !== 0) return minDiff;
        const maxDiff = areaSortKey(a.max_area) - areaSortKey(b.max_area);
        if (maxDiff !== 0) return maxDiff;
    }

    return nameCompare(a, b);
};

/**
 * Sort categories for the Layout 2 display.
 *
 * When `categoryOrder` (an ordered list of category slugs configured in the
 * CMS) is provided, those categories render first in that exact order; any
 * categories not listed fall back to the default order behind them. When it is
 * empty or omitted, the default order applies to all categories.
 */
export const sortCategories = (
    categories: UnitCategory[],
    categoryOrder?: string[],
): UnitCategory[] => {
    const byDefault = [...categories].sort(defaultCompare);

    if (!categoryOrder || categoryOrder.length === 0) {
        return byDefault;
    }

    const rank = new Map(categoryOrder.map((slug, index) => [slug, index]));
    const listed: UnitCategory[] = [];
    const unlisted: UnitCategory[] = [];

    for (const category of byDefault) {
        if (rank.has(category.slug)) {
            listed.push(category);
        } else {
            unlisted.push(category);
        }
    }

    listed.sort((a, b) => (rank.get(a.slug) ?? 0) - (rank.get(b.slug) ?? 0));

    return [...listed, ...unlisted];
};
